import { useEffect, useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import {
  Alert,
  App as AntdApp,
  Breadcrumb,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Popconfirm,
  Popover,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  InfoCircleOutlined,
  InsertRowRightOutlined,
  PlusOutlined,
  RightOutlined,
  SaveOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { supabase, type Tables } from '@/lib/supabase'
import { useIsMobile } from '@/lib/hooks'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import {
  TRANSCODE_STATUS_COLORS,
  TRANSCODE_STATUS_LABELS,
  summarizeEnqueue,
} from '@/lib/transcodes'
import type { TranscodeRow } from '@/lib/transcodes'
import { enqueueTranscodes } from '@/lib/transcodes-rpc'
import {
  attachPlaySourceBaseUrls,
  channelColKeyId,
  channelColumnLabel,
  cleanPastedLines,
  cleanPath,
  displayEpisodeTitle,
  mapPublicPlaySources,
  mergeChannelCols,
  pastePrefixAliases,
  pruneAbsorbedExtraCols,
  pruneDraftsBySource,
  type ChannelColKey,
  type PlaySourceOption,
  type PlaySourceUrlRow,
} from './-episodes-matrix'
import {
  channelGuessKey,
  guessLinePath,
  indexPreviousChannelPaths,
  type PreviousChannelPath,
} from './-episodes-path-guess'

/* ────────────────────────────────────────────────────────────────
   读写路径（项目约定见 CLAUDE.md «读写分流»）

     读：走 RLS / PostgREST —— 下面三个 useQuery
     写：走 RPC admin.upsert_episodes —— 下面 useMutation
         （admin schema，调用方式：supabase.schema('admin').rpc(...)）

   为什么写不走 RLS 直 upsert：
     ① 事务原子：episodes + episode_play_urls 是两张表，直写至少两次
        round-trip，中间炸就脏状态
     ② 丢审计：RPC 里有 insert into moderation.logs，直写丢掉
     ③ 丢 channel mode：RPC 内置 merge / replace 语义
     ④ 要给 admin 写 episodes 和 episode_play_urls 两张表的 RLS 策略
   备选写法（不采用，仅供对照）：
     await supabase.from('episodes').upsert({...}, { onConflict: '...' })
     await supabase.from('episode_play_urls').upsert([...], { onConflict: '...' })
   ──────────────────────────────────────────────────────────────── */

// ?add=1 自动打开追更 Drawer（来自仪表盘"今日更新"卡片的快捷入口）
const searchSchema = z.object({
  add: z.coerce.number().int().optional().catch(undefined),
})

export const Route = createFileRoute('/_authed/anime_/$animeId/episodes')({
  component: AnimeEpisodesPage,
  validateSearch: searchSchema,
})

type EpisodeRow = Tables<'episodes'> & {
  episode_play_urls: Array<Tables<'episode_play_urls'>>
}
type PlaySource = PlaySourceOption

type ColKey = ChannelColKey
const colKeyId = channelColKeyId

/* RPC payload 类型（admin.upsert_episodes 在 admin schema，supabase-js 默认
   类型只覆盖 public schema，所以 .schema('admin') 用了 `as never` 让 TS 跳过；
   下次跑 supabase gen types --schema admin 把 admin schema 类型纳入后可清理） */
interface ChannelPayload {
  source_code: string
  path: string
  quality: string | null
  is_active?: boolean
  sort_order?: number
}
interface EpisodePayload {
  episode_number: number
  kind: string
  title?: string | null
  channels?: Array<ChannelPayload>
}
interface UpsertResult {
  ok: boolean
  error?: string
  detail?: string
  results?: unknown
}

/** 删线路弹窗的可选项：本番剧里真实占用了至少一集的线路。 */
interface RemovableSource {
  source_id: number
  code: string
  name: string
  /** 该线路在本番剧下的 episode_play_urls 行数（将删条数） */
  urlCount: number
  /** 有该线路地址的集数 */
  episodeCount: number
  /** 该线路在本番剧出现过的画质（空数组 = 只有默认画质） */
  qualities: Array<string>
}

/* 元数据就地编辑的 patch。与 admin.upsert_episodes 的 key-presence 语义对齐：
   patch 里出现的 key 才写进 payload（null = 显式清空），缺失的 key 不动。 */
interface EpisodeMetaPatch {
  title?: string | null
  bangumi_ep_id?: number | null
  dandan_episode_id?: number | null
}

/* 清空同步 ID 必须连 synced_at 一起清：
   - dandan：C 端播放页只在 dandan_episode_id IS NULL 时触发 sync-dandan-mapping，
     且完结番以 episode.dandan_synced_at 判断"从未尝试过"，只清 ID 不清时间
     会被"已确认无映射结果定终身"的缓存分支挡住，永远不重新映射；
   - bgm：下一轮 bangumi-finished-episodes 按 sort/ep/order 启发式重新配对，
     清 synced_at 只是让"上次同步"展示不再误导。 */
function buildEpisodeMetaPayload(
  episode_number: number,
  kind: string,
  patch: EpisodeMetaPatch,
): Record<string, unknown> {
  const payload: Record<string, unknown> = { episode_number, kind }
  if (patch.title !== undefined) payload.title = patch.title ?? ''
  if (patch.bangumi_ep_id !== undefined) {
    payload.bangumi_ep_id = patch.bangumi_ep_id
    if (patch.bangumi_ep_id === null) payload.bangumi_synced_at = null
  }
  if (patch.dandan_episode_id !== undefined) {
    payload.dandan_episode_id = patch.dandan_episode_id
    if (patch.dandan_episode_id === null) payload.dandan_synced_at = null
  }
  return payload
}

function AnimeEpisodesPage() {
  const { animeId: animeIdStr } = Route.useParams()
  const { add: addParam } = Route.useSearch()
  const navigate = useNavigate()
  const animeId = Number(animeIdStr)
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  // < 992px：矩阵大表换成按集纵向列表（每集展开编辑各线路），只渲染一种
  const isMobile = useIsMobile()

  /* ── READ ① 番剧主条目（标题/集数） ─────────────────────────── */
  const animeQuery = useQuery({
    queryKey: ['anime', animeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('animes')
        .select('id, title, total_episodes, current_episodes')
        .eq('id', animeId)
        .single()
      if (error) throw error
      return data
    },
    enabled: Number.isFinite(animeId),
  })

  /* ── READ ② 剧集 + 嵌套的 play_urls（一次 PostgREST 关联查） ── */
  const episodesQuery = useQuery({
    queryKey: ['episodes', animeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('episodes')
        .select('*, episode_play_urls(*)')
        .eq('anime_id', animeId)
        .order('kind', { ascending: true })
        .order('episode_number', { ascending: true })
      if (error) throw error
      return (data ?? []) as Array<EpisodeRow>
    },
    enabled: Number.isFinite(animeId),
  })

  /* ── READ ③ 线路显示名：public.public_play_sources ───────────
     不要直查 play_sources.select('*')：base_url 等 URL 列对
     authenticated 不可读，PostgREST 会整段 403，列头就退化成序号。
     视图只暴露 id / code / name / sort_order（已启用源）。 */
  const sourcesQuery = useQuery({
    queryKey: ['public_play_sources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_play_sources')
        .select('id, code, name, sort_order')
        .order('sort_order', { ascending: true })
      if (error) throw error
      return mapPublicPlaySources(data ?? [])
    },
  })

  /* ── READ ③b 线路 base_url：admin.list_play_sources ──────────
     play_sources.base_url 对 authenticated 不可读，只能走 admin RPC。
     失败不挡名称：弹窗仍能选源，只是不能展示/裁前缀。 */
  const sourceUrlsQuery = useQuery({
    queryKey: ['admin_play_sources'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_play_sources')
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error ?? 'unknown')
      return (data.rows ?? []) as Array<PlaySourceUrlRow>
    },
  })

  const sources = useMemo(
    () =>
      attachPlaySourceBaseUrls(
        sourcesQuery.data ?? [],
        sourceUrlsQuery.data ?? [],
      ),
    [sourcesQuery.data, sourceUrlsQuery.data],
  )

  /* ── READ ④ 本番剧集的转码状态（admin RPC 桥接，表无 RLS 读策略） ── */
  const transcodesQuery = useQuery({
    queryKey: ['admin_transcodes', { animeId }],
    refetchInterval: 10_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_transcodes', {
          p_status: null,
          p_keyword: null,
          p_anime_id: animeId,
          p_limit: 500,
          p_offset: 0,
        })
      if (error) throw error
      if (!data.ok) throw new Error(data.error ?? 'unknown')
      // Partial：索引访问类型如实带 undefined（无转码行的剧集）
      const byId: Partial<Record<number, TranscodeRow>> = {}
      for (const row of (data.rows ?? []) as TranscodeRow[]) {
        byId[row.episode_id] = row
      }
      return byId
    },
    enabled: Number.isFinite(animeId),
  })

  /* ── WRITE  admin.upsert_episodes RPC ────────────────────────── */
  const upsertMutation = useMutation({
    mutationFn: async (params: {
      episodes: Array<EpisodePayload>
      channel_mode?: 'merge' | 'replace'
    }) => {
      // admin schema 还没生成进 database.types.ts，先 cast 跳过类型检查
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('upsert_episodes', {
          p_anime_id: animeId,
          p_episodes: params.episodes,
          p_channel_mode: params.channel_mode ?? 'merge',
        })
      if (error) throw error
      const result = data as unknown as UpsertResult
      if (!result.ok) {
        throw new Error(`RPC error=${result.error ?? 'unknown'}`)
      }
      return result
    },
    onSuccess: () => {
      message.success('已保存')
      qc.invalidateQueries({ queryKey: ['episodes', animeId] })
      setDrafts({})
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  })

  /* ── DELETE  admin.delete_episode / admin.delete_play_url ───── */
  const deleteEpisodeMutation = useMutation({
    mutationFn: async (episodeId: number) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_episode', { p_episode_id: episodeId })
      if (error) throw error
      const result = data as { ok: boolean; error?: string }
      if (!result.ok) throw new Error(`RPC error=${result.error ?? 'unknown'}`)
      return result
    },
    onSuccess: () => {
      message.success('已删除整集')
      qc.invalidateQueries({ queryKey: ['episodes', animeId] })
      qc.invalidateQueries({ queryKey: ['anime', animeId] }) // current_episodes 变了
    },
    onError: (e: Error) => message.error(e.message || '删除失败'),
  })

  const deletePlayUrlMutation = useMutation({
    mutationFn: async (params: {
      episodeId: number
      sourceCode: string
      quality: string | null
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_play_url', {
          p_episode_id: params.episodeId,
          p_source_code: params.sourceCode,
          p_quality: params.quality,
        })
      if (error) throw error
      const result = data as { ok: boolean; error?: string }
      if (!result.ok) throw new Error(`RPC error=${result.error ?? 'unknown'}`)
      return result
    },
    onSuccess: () => {
      message.success('已删除该线路')
      qc.invalidateQueries({ queryKey: ['episodes', animeId] })
    },
    onError: (e: Error) => message.error(e.message || '删除失败'),
  })

  /* ── DELETE admin.delete_anime_play_source：摘掉本番一整条线路 ──
     只删本番 episode_play_urls 里该 source 的全部行（含所有画质），
     全局 play_sources 记录与剧集本身都不动。 */
  const deleteAnimeSourceMutation = useMutation({
    mutationFn: async (params: { sourceId: number; sourceCode: string }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_anime_play_source', {
          p_anime_id: animeId,
          p_source_code: params.sourceCode,
        })
      if (error) throw error
      const result = data as {
        ok: boolean
        error?: string
        deleted_count?: number
      }
      if (!result.ok) throw new Error(`RPC error=${result.error ?? 'unknown'}`)
      return result
    },
    onSuccess: (result, vars) => {
      message.success(`已摘除线路，清理 ${result.deleted_count ?? 0} 条地址`)
      // 指向已删线路的未保存草稿 / 未落地列一并清掉，
      // 否则下次保存会把非空草稿当新 path 重新 upsert，线路原地复活
      setDrafts((prev) => pruneDraftsBySource(prev, vars.sourceId))
      setExtraColKeys((prev) =>
        prev.filter((k) => k.source_id !== vars.sourceId),
      )
      qc.invalidateQueries({ queryKey: ['episodes', animeId] })
      qc.invalidateQueries({ queryKey: ['anime', animeId] }) // current_episodes 可能变
      qc.invalidateQueries({ queryKey: ['admin_transcodes'] }) // source_play_url_id 脱钩
    },
    onError: (e: Error) => message.error(e.message || '摘除失败'),
  })

  /* ── 元数据就地编辑（标题 / bgm ID / dandan ID）：immediate-commit，
        不进 channels 草稿；单集单字段 payload，走同一 upsert RPC ──────── */
  const updateEpisodeMetaMutation = useMutation({
    mutationFn: async (params: {
      episode_number: number
      kind: string
      patch: EpisodeMetaPatch
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('upsert_episodes', {
          p_anime_id: animeId,
          p_episodes: [
            buildEpisodeMetaPayload(
              params.episode_number,
              params.kind,
              params.patch,
            ),
          ],
          p_channel_mode: 'merge',
        })
      if (error) throw error
      const result = data as UpsertResult
      if (!result.ok) {
        // bangumi_ep_id 跨番剧全局唯一，撞车时把 DB detail 带出来
        throw new Error(
          result.detail
            ? `RPC ${result.error}：${result.detail}`
            : `RPC error=${result.error ?? 'unknown'}`,
        )
      }
      return result
    },
    onSuccess: () => {
      message.success('已保存')
      qc.invalidateQueries({ queryKey: ['episodes', animeId] })
      // 不动 drafts —— 元数据编辑不涉及 channel
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  })

  /* ── WRITE admin.enqueue_transcodes（手动入队 / 重试，分片 ≤100） ── */
  const enqueueMutation = useMutation({
    mutationFn: enqueueTranscodes,
    onSuccess: (out) => {
      message.success(summarizeEnqueue(out))
      void qc.invalidateQueries({ queryKey: ['admin_transcodes'] })
    },
    onError: (e: Error) => message.error(`入队失败：${e.message}`),
  })

  /* ── 派生：当前矩阵的列（source × quality 组合） ─────────────── */
  const derivedColKeys = useMemo<Array<ColKey>>(() => {
    const seen = new Map<string, ColKey>()
    for (const ep of episodesQuery.data ?? []) {
      for (const u of ep.episode_play_urls) {
        const k: ColKey = { source_id: u.source_id, quality: u.quality }
        seen.set(colKeyId(k), k)
      }
    }
    const sourceOrder = new Map<number, number>()
    for (const s of sources) sourceOrder.set(s.id, s.sort_order ?? 0)
    return Array.from(seen.values()).sort((a, b) => {
      const o =
        (sourceOrder.get(a.source_id) ?? 0) -
        (sourceOrder.get(b.source_id) ?? 0)
      if (o !== 0) return o
      return (a.quality ?? '').localeCompare(b.quality ?? '')
    })
  }, [episodesQuery.data, sources])

  /* 本地加的线路列：还没有任何 play_url 时不会出现在 derived 里。
     保存成功、query 带回新行后由 effect 剪掉已落地的 extra。 */
  const [extraColKeys, setExtraColKeys] = useState<Array<ColKey>>([])
  const colKeys = useMemo(
    () => mergeChannelCols(derivedColKeys, extraColKeys),
    [derivedColKeys, extraColKeys],
  )
  useEffect(() => {
    setExtraColKeys((prev) => {
      const next = pruneAbsorbedExtraCols(prev, derivedColKeys)
      return next.length === prev.length ? prev : next
    })
  }, [derivedColKeys])

  /* ── 派生：本番剧实际占用的线路（删线路弹窗的可选集合） ──────
     只列 episode_play_urls 里真有行的 source——本地加的 extra 列
     没有 DB 行，RPC 只会回 channel_not_found，不列出来。
     code 优先取 public 视图，停用源回退到 admin.list_play_sources。 */
  const removableSources = useMemo<Array<RemovableSource>>(() => {
    const usage = new Map<
      number,
      { urlCount: number; episodeIds: Set<number>; qualities: Set<string> }
    >()
    for (const ep of episodesQuery.data ?? []) {
      for (const u of ep.episode_play_urls) {
        let s = usage.get(u.source_id)
        if (!s) {
          s = { urlCount: 0, episodeIds: new Set(), qualities: new Set() }
          usage.set(u.source_id, s)
        }
        s.urlCount += 1
        s.episodeIds.add(ep.id)
        if (u.quality) s.qualities.add(u.quality)
      }
    }
    if (usage.size === 0) return []

    const metaById = new Map<number, { code?: string; name?: string }>()
    for (const s of sources) metaById.set(s.id, { code: s.code, name: s.name })
    for (const r of sourceUrlsQuery.data ?? []) {
      if (!metaById.has(r.id) && r.code) {
        metaById.set(r.id, { code: r.code, name: r.name ?? undefined })
      }
    }

    const order = new Map<number, number>()
    for (const s of sources) order.set(s.id, s.sort_order ?? 0)

    const out: Array<RemovableSource> = []
    for (const [sourceId, u] of usage) {
      const meta = metaById.get(sourceId)
      if (!meta?.code) continue // 没 code 调不了 RPC，不应发生
      out.push({
        source_id: sourceId,
        code: meta.code,
        name: meta.name?.trim() || meta.code,
        urlCount: u.urlCount,
        episodeCount: u.episodeIds.size,
        qualities: Array.from(u.qualities).sort(),
      })
    }
    return out.sort(
      (a, b) => (order.get(a.source_id) ?? 0) - (order.get(b.source_id) ?? 0),
    )
  }, [episodesQuery.data, sources, sourceUrlsQuery.data])

  function addChannelColumn(k: ColKey) {
    const id = colKeyId(k)
    if (colKeys.some((c) => colKeyId(c) === id)) {
      message.info('该线路列已在表里')
      return false
    }
    setExtraColKeys((prev) => mergeChannelCols(prev, [k]))
    return true
  }

  /* ── 脏检测：episode_id -> colKeyId -> 新 path ───────────────── */
  const [drafts, setDrafts] = useState<Record<number, Record<string, string>>>(
    {},
  )

  function removeExtraColumn(k: ColKey) {
    const id = colKeyId(k)
    setExtraColKeys((prev) => prev.filter((c) => colKeyId(c) !== id))
    setDrafts((prev) => {
      let changed = false
      const next: typeof prev = {}
      for (const [epId, cells] of Object.entries(prev)) {
        if (cells[id] === undefined) {
          next[Number(epId)] = cells
          continue
        }
        changed = true
        const rest = { ...cells }
        delete rest[id]
        next[Number(epId)] = rest
      }
      return changed ? next : prev
    })
  }

  function getCellOriginal(ep: EpisodeRow, k: ColKey): string {
    return (
      ep.episode_play_urls.find(
        (u) =>
          u.source_id === k.source_id &&
          (u.quality ?? '') === (k.quality ?? ''),
      )?.path ?? ''
    )
  }
  function getCellValue(ep: EpisodeRow, k: ColKey): string {
    const draft = drafts[ep.id]?.[colKeyId(k)]
    return draft !== undefined ? draft : getCellOriginal(ep, k)
  }
  function setCellValue(ep: EpisodeRow, k: ColKey, value: string) {
    setDrafts((prev) => ({
      ...prev,
      [ep.id]: { ...(prev[ep.id] ?? {}), [colKeyId(k)]: value },
    }))
  }
  function isCellDirty(ep: EpisodeRow, k: ColKey): boolean {
    const draft = drafts[ep.id]?.[colKeyId(k)]
    return draft !== undefined && draft !== getCellOriginal(ep, k)
  }

  const dirtyCount = useMemo(() => {
    let n = 0
    for (const ep of episodesQuery.data ?? []) {
      for (const k of colKeys) if (isCellDirty(ep, k)) n += 1
    }
    return n
  }, [drafts, episodesQuery.data, colKeys])

  /* ── 提交：只把"有改动的 episode"打包，每集发送全部非空格子 ─── */
  function handleSave() {
    if (dirtyCount === 0) return
    const sourceById = new Map<number, PlaySource>()
    for (const s of sources) sourceById.set(s.id, s)

    const payload: Array<EpisodePayload> = []
    for (const ep of episodesQuery.data ?? []) {
      const epDirty = colKeys.some((k) => isCellDirty(ep, k))
      if (!epDirty) continue

      const channels: Array<ChannelPayload> = []
      for (const k of colKeys) {
        const value = getCellValue(ep, k).trim()
        if (!value) continue
        const source = sourceById.get(k.source_id)
        if (!source) continue
        channels.push({
          source_code: source.code,
          path: cleanPath(value, source.base_url ?? '', source.code),
          quality: k.quality,
        })
      }
      payload.push({
        episode_number: ep.episode_number,
        kind: ep.kind,
        channels,
      })
    }

    if (payload.length === 0) {
      message.info('没有有效改动')
      return
    }
    upsertMutation.mutate({ episodes: payload, channel_mode: 'merge' })
  }

  /* ── 同步 ID 列（bgm / dandan）：就地编辑，清空 = 撤销同步锚点交回
        同步任务重新配对。改 ID 是敏感操作，RPC 侧会把 old → new 记进
        moderation.logs metadata.id_edits ──────────────────────────── */
  function makeSyncIdColumn(
    field: 'bgm' | 'dandan',
  ): ColumnsType<EpisodeRow>[number] {
    const isBgm = field === 'bgm'
    return {
      title: isBgm ? 'bgm ID' : 'dandan ID',
      key: isBgm ? 'bangumi_ep_id' : 'dandan_episode_id',
      width: 110,
      render: (_: unknown, ep: EpisodeRow) => renderSyncIdValue(field, ep),
    }
  }

  /* 同步 ID 单元格：桌面表格列与手机按集卡片共用 */
  function renderSyncIdValue(field: 'bgm' | 'dandan', ep: EpisodeRow) {
    const isBgm = field === 'bgm'
    const value = isBgm ? ep.bangumi_ep_id : ep.dandan_episode_id
    const syncedAt = isBgm ? ep.bangumi_synced_at : ep.dandan_synced_at
    return (
      <Tooltip
        title={
          <div style={{ fontSize: 12 }}>
            <div>
              {isBgm
                ? 'bgm.tv 集 ID，元数据同步主键'
                : '弹弹play 集 ID，弹幕源映射'}
            </div>
            <div>
              {isBgm ? '上次同步：' : '上次映射：'}
              {syncedAt ? dayjs(syncedAt).format('YYYY-MM-DD HH:mm') : '从未'}
            </div>
            <div>清空后由同步任务重新配对</div>
          </div>
        }
      >
        <Typography.Text
          style={{
            color: value != null ? undefined : 'var(--ant-color-text-tertiary)',
            fontFamily: 'monospace',
          }}
          editable={{
            text: value != null ? String(value) : '',
            maxLength: 18,
            triggerType: ['icon', 'text'],
            onChange: (next) => {
              const trimmed = next.trim()
              const before = value != null ? String(value) : ''
              if (trimmed === before) return
              const patch: EpisodeMetaPatch = isBgm
                ? { bangumi_ep_id: null }
                : { dandan_episode_id: null }
              if (trimmed === '') {
                updateEpisodeMetaMutation.mutate({
                  episode_number: ep.episode_number,
                  kind: ep.kind,
                  patch,
                })
                return
              }
              const parsed = Number(trimmed)
              if (!Number.isSafeInteger(parsed) || parsed <= 0) {
                message.error(
                  isBgm
                    ? 'bgm 集 ID 必须是正整数'
                    : 'dandan 集 ID 必须是正整数',
                )
                return
              }
              updateEpisodeMetaMutation.mutate({
                episode_number: ep.episode_number,
                kind: ep.kind,
                patch: isBgm
                  ? { bangumi_ep_id: parsed }
                  : { dandan_episode_id: parsed },
              })
            },
          }}
        >
          {value != null ? value : isBgm ? '未配对' : '未映射'}
        </Typography.Text>
      </Tooltip>
    )
  }

  /* 标题就地编辑：桌面表格列与手机按集卡片共用 */
  function renderTitleCell(ep: EpisodeRow) {
    const t = ep.title
    const shown = displayEpisodeTitle(t, ep.title_original)
    return (
      <div className="admin-episode-title-cell">
        <Typography.Text
          style={{
            maxWidth: isMobile ? '100%' : 220,
            color: shown.isPlaceholder
              ? 'var(--ant-color-text-tertiary)'
              : undefined,
          }}
          ellipsis={{ tooltip: shown.primary }}
          editable={{
            text: t ?? '',
            tooltip: '点击编辑标题',
            maxLength: 200,
            triggerType: ['icon', 'text'],
            onChange: (next) => {
              const trimmed = next.trim()
              const before = (t ?? '').trim()
              if (trimmed === before) return
              updateEpisodeMetaMutation.mutate({
                episode_number: ep.episode_number,
                kind: ep.kind,
                patch: { title: trimmed || null },
              })
            },
          }}
        >
          {shown.primary}
        </Typography.Text>
        {shown.secondary ? (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block' }}
            ellipsis={{ tooltip: shown.secondary }}
          >
            {shown.secondary}
          </Typography.Text>
        ) : null}
      </div>
    )
  }

  /* 转码状态 + 入队/重试：桌面表格列与手机按集卡片共用 */
  function renderTranscodeCell(row: EpisodeRow) {
    if (!transcodesQuery.data) {
      return <Typography.Text type="secondary">—</Typography.Text>
    }
    const t = transcodesQuery.data[row.id]
    if (!t) {
      return (
        <Button
          size="small"
          type="link"
          disabled={enqueueMutation.isPending}
          onClick={() => enqueueMutation.mutate([row.id])}
        >
          入队转码
        </Button>
      )
    }
    const tag = (
      <Tag color={TRANSCODE_STATUS_COLORS[t.status]}>
        {TRANSCODE_STATUS_LABELS[t.status]}
      </Tag>
    )
    if (t.status === 'failed' || t.status === 'evicted') {
      return (
        <Space size={4}>
          {t.status === 'failed' && t.last_error ? (
            <Tooltip title={t.last_error}>{tag}</Tooltip>
          ) : (
            tag
          )}
          <Button
            size="small"
            type="link"
            disabled={enqueueMutation.isPending}
            onClick={() => enqueueMutation.mutate([row.id])}
          >
            重试
          </Button>
        </Space>
      )
    }
    return tag
  }

  /* ── 表格列：基础列 + 动态线路列 ─────────────────────────────── */
  const columns: ColumnsType<EpisodeRow> = useMemo(() => {
    const base: ColumnsType<EpisodeRow> = [
      {
        title: '类型',
        dataIndex: 'kind',
        width: 72,
        fixed: 'left',
        render: (k: string) => <Tag>{k}</Tag>,
      },
      {
        title: '集',
        dataIndex: 'episode_number',
        width: 72,
        fixed: 'left',
        render: (n: number) => <Typography.Text strong>{n}</Typography.Text>,
      },
      {
        title: '标题',
        dataIndex: 'title',
        width: 240,
        render: (_: string | null, ep: EpisodeRow) => renderTitleCell(ep),
      },
      makeSyncIdColumn('bgm'),
      makeSyncIdColumn('dandan'),
    ]
    const extraIds = new Set(extraColKeys.map(colKeyId))
    const sourceCols: ColumnsType<EpisodeRow> = colKeys.map((k) => {
      const src = sources.find((s) => s.id === k.source_id)
      const label = channelColumnLabel(k.source_id, src, k.quality)
      const isExtra = extraIds.has(colKeyId(k))
      return {
        title: (
          <div className="admin-channel-col-title">
            <span className="admin-channel-col-title__name" title={label.name}>
              {label.name}
            </span>
            <span className="admin-channel-col-title__meta">
              {label.meta}
              {isExtra ? (
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeExtraColumn(k)
                  }}
                  style={{ padding: 0, height: 'auto', fontSize: 11 }}
                >
                  移除列
                </Button>
              ) : null}
            </span>
          </div>
        ),
        key: colKeyId(k),
        width: 280,
        render: (_: unknown, ep: EpisodeRow) => {
          const hasDbRow = getCellOriginal(ep, k).length > 0
          return (
            <Space.Compact style={{ width: '100%' }}>
              <Input
                size="small"
                placeholder={hasDbRow ? 'path' : '填写以新建路径'}
                status={isCellDirty(ep, k) ? 'warning' : undefined}
                value={getCellValue(ep, k)}
                onChange={(e) =>
                  setCellValue(
                    ep,
                    k,
                    cleanPath(e.target.value, src?.base_url ?? '', src?.code),
                  )
                }
              />
              {hasDbRow && src ? (
                <Popconfirm
                  title="删除该线路？"
                  description={`将从 DB 删除 ${src.name}${k.quality ? ` · ${k.quality}` : ''} 这条线路`}
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                  onConfirm={() =>
                    deletePlayUrlMutation.mutate({
                      episodeId: ep.id,
                      sourceCode: src.code,
                      quality: k.quality,
                    })
                  }
                >
                  <Button
                    size="small"
                    icon={<DeleteOutlined />}
                    aria-label="删除该线路"
                  />
                </Popconfirm>
              ) : null}
            </Space.Compact>
          )
        },
      }
    })
    const transcodeCol: ColumnsType<EpisodeRow>[number] = {
      title: '转码',
      key: 'transcode',
      width: 120,
      render: (_: unknown, row: EpisodeRow) => renderTranscodeCell(row),
    }
    const actionCol: ColumnsType<EpisodeRow>[number] = {
      title: '操作',
      key: '__actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, ep: EpisodeRow) => {
        const lineCount = ep.episode_play_urls.length
        return (
          <Popconfirm
            title="删除整集？"
            description={
              <span>
                第 <b>{ep.episode_number}</b> 集（{ep.kind}）及其{' '}
                <b>{lineCount}</b> 条线路将被永久删除，不可恢复。
              </span>
            }
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
            placement="leftTop"
            onConfirm={() => deleteEpisodeMutation.mutate(ep.id)}
          >
            <Button
              size="small"
              danger
              type="text"
              icon={<DeleteOutlined />}
              aria-label="删除整集"
            />
          </Popconfirm>
        )
      },
    }
    return [...base, ...sourceCols, transcodeCol, actionCol]
  }, [
    colKeys,
    extraColKeys,
    drafts,
    sources,
    episodesQuery.data,
    transcodesQuery.data,
    enqueueMutation,
  ])

  const isLoading =
    animeQuery.isLoading || episodesQuery.isLoading || sourcesQuery.isLoading

  /* ── 手机纵向视图：一集一张卡，折叠时用线路 chip 概览「哪些线路有地址」，
        展开后逐条编辑 path / 标题 / 同步 ID。草稿与桌面矩阵共用 drafts。 */
  const [expandedEps, setExpandedEps] = useState<Set<number>>(() => new Set())
  const allEpisodeIds = (episodesQuery.data ?? []).map((e) => e.id)
  const allExpanded =
    allEpisodeIds.length > 0 && allEpisodeIds.every((id) => expandedEps.has(id))

  function toggleEpisode(id: number) {
    setExpandedEps((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function confirmDeletePlayUrl(ep: EpisodeRow, k: ColKey, src: PlaySource) {
    modal.confirm({
      title: '删除该线路？',
      content: `将从 DB 删除第 ${ep.episode_number} 集的 ${src.name}${k.quality ? ` · ${k.quality}` : ''} 这条线路`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () =>
        deletePlayUrlMutation.mutate({
          episodeId: ep.id,
          sourceCode: src.code,
          quality: k.quality,
        }),
    })
  }

  function renderMobileEpisode(ep: EpisodeRow) {
    const expanded = expandedEps.has(ep.id)
    const shown = displayEpisodeTitle(ep.title, ep.title_original)
    const epDirty = colKeys.filter((k) => isCellDirty(ep, k)).length
    const bodyId = `episodes-mobile-ep-${ep.id}`
    return (
      <article
        className={[
          'episodes-mobile-ep',
          epDirty > 0 ? 'episodes-mobile-ep--dirty' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          type="button"
          className="episodes-mobile-ep__toggle"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => toggleEpisode(ep.id)}
        >
          <span className="episodes-mobile-ep__head">
            <span className="episodes-mobile-ep__num">
              {ep.kind !== 'main' ? <Tag>{ep.kind}</Tag> : null}第{' '}
              {ep.episode_number} 集
            </span>
            <span
              className={[
                'episodes-mobile-ep__title',
                shown.isPlaceholder ? 'episodes-mobile-ep__title--muted' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {shown.primary}
            </span>
            <RightOutlined
              className="episodes-mobile-ep__chevron"
              rotate={expanded ? 90 : 0}
            />
          </span>
          <span className="episodes-mobile-ep__chips">
            {colKeys.length === 0 ? (
              <span className="episodes-mobile-chip">无线路</span>
            ) : (
              colKeys.map((k) => {
                const src = sources.find((s) => s.id === k.source_id)
                const label = channelColumnLabel(k.source_id, src, k.quality)
                const filled = getCellValue(ep, k).trim().length > 0
                const dirty = isCellDirty(ep, k)
                return (
                  <span
                    key={colKeyId(k)}
                    className={[
                      'episodes-mobile-chip',
                      filled ? 'episodes-mobile-chip--on' : '',
                      dirty ? 'episodes-mobile-chip--dirty' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {label.name}
                    {k.quality ? ` · ${k.quality}` : ''}
                  </span>
                )
              })
            )}
            {epDirty > 0 ? (
              <span className="episodes-mobile-ep__dirty">
                {epDirty} 处未保存
              </span>
            ) : null}
          </span>
        </button>

        {expanded ? (
          <div id={bodyId} className="episodes-mobile-ep__body">
            <div className="episodes-mobile-ep__fields">
              <span className="episodes-mobile-ep__label">标题</span>
              <div className="episodes-mobile-ep__value">
                {renderTitleCell(ep)}
              </div>
              <span className="episodes-mobile-ep__label">bgm ID</span>
              <div className="episodes-mobile-ep__value">
                {renderSyncIdValue('bgm', ep)}
              </div>
              <span className="episodes-mobile-ep__label">dandan ID</span>
              <div className="episodes-mobile-ep__value">
                {renderSyncIdValue('dandan', ep)}
              </div>
            </div>

            {colKeys.length > 0 ? (
              <div className="episodes-mobile-ep__lines">
                {colKeys.map((k) => {
                  const src = sources.find((s) => s.id === k.source_id)
                  const label = channelColumnLabel(k.source_id, src, k.quality)
                  const hasDbRow = getCellOriginal(ep, k).length > 0
                  const dirty = isCellDirty(ep, k)
                  return (
                    <div key={colKeyId(k)} className="episodes-mobile-line">
                      <div className="episodes-mobile-line__label">
                        <span className="episodes-mobile-line__name">
                          {label.name}
                        </span>
                        <span className="episodes-mobile-line__meta">
                          {label.meta}
                        </span>
                        {dirty ? (
                          <span className="episodes-mobile-ep__dirty">
                            未保存
                          </span>
                        ) : null}
                      </div>
                      <Space.Compact style={{ width: '100%' }}>
                        <Input
                          placeholder={hasDbRow ? 'path' : '填写以新建路径'}
                          status={dirty ? 'warning' : undefined}
                          value={getCellValue(ep, k)}
                          aria-label={`${label.name} 路径`}
                          className="episodes-mobile-line__input"
                          onChange={(e) =>
                            setCellValue(
                              ep,
                              k,
                              cleanPath(
                                e.target.value,
                                src?.base_url ?? '',
                                src?.code,
                              ),
                            )
                          }
                        />
                        {hasDbRow && src ? (
                          <Button
                            icon={<DeleteOutlined />}
                            aria-label="删除该线路"
                            onClick={() => confirmDeletePlayUrl(ep, k, src)}
                          />
                        ) : null}
                      </Space.Compact>
                    </div>
                  )
                })}
              </div>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                还没有线路：点上方「加线路」选一个播放源。
              </Typography.Text>
            )}

            <AdminRecordActions
              leading={
                <>
                  <span>转码</span>
                  {renderTranscodeCell(ep)}
                </>
              }
              actions={[
                {
                  key: 'delete',
                  label: '删除整集',
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => deleteEpisodeMutation.mutate(ep.id),
                  confirm: {
                    title: '删除整集？',
                    description: (
                      <span>
                        第 <b>{ep.episode_number}</b> 集（{ep.kind}）及其{' '}
                        <b>{ep.episode_play_urls.length}</b>{' '}
                        条线路将被永久删除，不可恢复。
                      </span>
                    ),
                    okText: '删除',
                  },
                },
              ]}
            />
          </div>
        ) : null}
      </article>
    )
  }

  /* ── 新增剧集 Drawer 状态 ──────────────────────────────────── */
  const [addOpen, setAddOpen] = useState(false)

  // ?add=1 一键打开：等 anime + sources + episodes 加载完再开
  // （drawer 依赖 current_episodes / sources / 上一集 path）
  useEffect(() => {
    if (
      addParam &&
      animeQuery.data &&
      sourcesQuery.data &&
      !episodesQuery.isLoading &&
      !addOpen
    ) {
      setAddOpen(true)
      // 清掉 search param，避免再次打开 / 刷新历史污染
      navigate({
        to: '/anime/$animeId/episodes',
        params: { animeId: animeIdStr },
        search: {},
        replace: true,
      })
    }
  }, [addParam, animeQuery.data, sourcesQuery.data, episodesQuery.isLoading])

  return (
    <div className="admin-page episodes-page">
      <div className="admin-sticky-toolbar">
        <div className="admin-toolbar-title">
          <div style={{ minWidth: 0 }}>
            <Breadcrumb
              items={[
                { title: <Link to="/anime">番剧</Link> },
                { title: animeQuery.data?.title ?? `#${animeId}` },
                { title: '剧集' },
              ]}
              style={{ marginBottom: 2, fontSize: 12 }}
            />
            <Space size={8} align="center">
              <div className="admin-toolbar-title__main">剧集管理</div>
              {episodesQuery.data ? (
                <Tag>共 {episodesQuery.data.length} 集</Tag>
              ) : null}
              {!isLoading && dirtyCount > 0 ? (
                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                  {dirtyCount} 处未保存改动
                </Typography.Text>
              ) : null}
              {!isLoading && dirtyCount === 0 ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  无未保存改动
                </Typography.Text>
              ) : null}
            </Space>
          </div>
        </div>

        <Space size={8} align="center" wrap>
          <Popconfirm
            title="全集入队转码？"
            description="已就绪/已在队列的集会自动跳过。"
            onConfirm={() =>
              enqueueMutation.mutate(
                (episodesQuery.data ?? []).map((e) => e.id),
              )
            }
          >
            {/* 手机上次要操作收成 icon-only（带 Tooltip + aria-label），只留「新增」「保存」带字 */}
            <Tooltip title={isMobile ? '全集入队转码' : undefined}>
              <Button
                icon={<ThunderboltOutlined />}
                loading={enqueueMutation.isPending}
                disabled={
                  !episodesQuery.data || episodesQuery.data.length === 0
                }
                aria-label="全集入队"
              >
                {isMobile ? null : '全集入队'}
              </Button>
            </Tooltip>
          </Popconfirm>
          <AddChannelPopover
            sources={sources.filter((s) => s.is_active !== false)}
            existing={colKeys}
            disabled={!sourcesQuery.data}
            compact={isMobile}
            onAdd={addChannelColumn}
          />
          <RemoveSourcePopover
            compact={isMobile}
            options={removableSources}
            disabled={!episodesQuery.data || removableSources.length === 0}
            submitting={deleteAnimeSourceMutation.isPending}
            onRemove={(s) =>
              deleteAnimeSourceMutation.mutate({
                sourceId: s.source_id,
                sourceCode: s.code,
              })
            }
          />
          <Button
            icon={<PlusOutlined />}
            onClick={() => setAddOpen(true)}
            disabled={!animeQuery.data || !sourcesQuery.data}
          >
            {isMobile ? '新增' : '新增剧集'}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={dirtyCount === 0}
            loading={upsertMutation.isPending}
            onClick={handleSave}
          >
            保存{dirtyCount > 0 ? `(${dirtyCount})` : ''}
          </Button>
        </Space>
      </div>

      <div className="admin-episode-hint">
        <InfoCircleOutlined />
        {isMobile ? (
          <span>
            点开一集即可编辑各线路 path；空线路填写后保存即新建。橙色 = 未保存。
          </span>
        ) : (
          <span>
            行 = 剧集，列 = 播放线路（源名 ·
            code）。空格填写后保存即新建路径。「加线路」加源列，「删线路」整列摘除（仅本番），「新增剧集」加一集。橙色
            = 未保存。
          </span>
        )}
      </div>

      {!isLoading &&
      (episodesQuery.data?.length ?? 0) > 0 &&
      colKeys.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="还没有线路列"
          description="点右上「加线路」选一个播放源，空单元格填写路径后保存即新建。"
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {isMobile ? (
        <>
          {/* 本地新加、尚未落库的线路：矩阵里在列头「移除列」，手机上集中放这里 */}
          {extraColKeys.length > 0 ? (
            <div className="episodes-mobile-extra">
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                新加线路（填 path 并保存后落库）：
              </Typography.Text>
              {extraColKeys.map((k) => {
                const src = sources.find((s) => s.id === k.source_id)
                const label = channelColumnLabel(k.source_id, src, k.quality)
                return (
                  <Tag
                    key={colKeyId(k)}
                    closable
                    closeIcon={<span aria-label="移除列">×</span>}
                    onClose={(e) => {
                      e.preventDefault()
                      removeExtraColumn(k)
                    }}
                  >
                    {label.name}
                  </Tag>
                )
              })}
            </div>
          ) : null}

          {allEpisodeIds.length > 0 ? (
            <div className="episodes-mobile-listbar">
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {colKeys.length} 条线路 · 点集展开编辑
              </Typography.Text>
              <Button
                size="small"
                type="link"
                onClick={() =>
                  setExpandedEps(
                    allExpanded ? new Set() : new Set(allEpisodeIds),
                  )
                }
              >
                {allExpanded ? '全部收起' : '全部展开'}
              </Button>
            </div>
          ) : null}

          <AdminMobileList
            rows={episodesQuery.data ?? []}
            rowKey={(ep) => ep.id}
            loading={isLoading}
            renderItem={renderMobileEpisode}
            emptyContent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  isLoading ? (
                    '加载中…'
                  ) : (
                    <Space direction="vertical" size={4}>
                      <Typography.Text strong>还没有剧集</Typography.Text>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        点上方「新增」加一集，或在抽屉里批量粘贴 URL
                      </Typography.Text>
                    </Space>
                  )
                }
              />
            }
          />

          {/* 有改动时吸底保存条：工具栏在手机上不吸顶，避免改到底部还得滚回去 */}
          {dirtyCount > 0 ? (
            <div className="admin-mobile-bulkbar">
              <span className="admin-mobile-bulkbar__count">
                {dirtyCount} 处未保存改动
              </span>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={upsertMutation.isPending}
                onClick={handleSave}
              >
                保存
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="admin-table-wrap">
          <Card className="admin-table-card">
            <Table<EpisodeRow>
              rowKey="id"
              size="small"
              loading={isLoading}
              pagination={false}
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={episodesQuery.data ?? []}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Space direction="vertical" size={4}>
                        <Typography.Text strong>还没有剧集</Typography.Text>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          点右上「新增剧集」加一集，或在抽屉里批量粘贴 URL
                        </Typography.Text>
                      </Space>
                    }
                  />
                ),
              }}
            />
          </Card>
        </div>
      )}

      <AddEpisodesDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        defaultStart={(animeQuery.data?.current_episodes ?? 0) + 1}
        sources={sources.filter((s) => s.is_active !== false)}
        existingChannels={colKeys}
        episodes={episodesQuery.data ?? []}
        allowEmptyChannels
        submitting={upsertMutation.isPending}
        onSubmit={(payload) => {
          upsertMutation.mutate(
            { episodes: payload, channel_mode: 'merge' },
            { onSuccess: () => setAddOpen(false) },
          )
        }}
      />
    </div>
  )
}

/* ════════════════════════════════════════════════════════════════
   新增剧集 Drawer：单集填路径，或按列粘贴 URL 批量导入

   工作流：
     1. 起始集号默认 = anime.current_episodes + 1（追下一集）
     2. 类型默认 main
     3. 列默认预填 = 本番剧既有出现过的所有线路；可加可删
     4. 每列一个 textarea，每行一条 URL（trim, 空行跳过）
     5. 提交时按列拼出 channels[]，按行汇总成 episodes[] → RPC merge
   ════════════════════════════════════════════════════════════════ */

interface PasteColumn {
  id: string
  source_id: number | null
  quality: string // '' 表示"默认/不分画质"，提交时转 null
  text: string
}

const KIND_OPTIONS = [
  { value: 'main', label: '正片 (main)' },
  { value: 'sp', label: '特别篇 (sp)' },
  { value: 'op', label: 'OP' },
  { value: 'ed', label: 'ED' },
]

function makeColumn(seed?: Partial<PasteColumn>): PasteColumn {
  return {
    id: newClientId(),
    source_id: seed?.source_id ?? null,
    quality: seed?.quality ?? '',
    text: seed?.text ?? '',
  }
}

/* ── 单集追更模式（默认）：每行就一条线路，path 用 prefix+input ───── */

interface SingleLine {
  id: string
  source_id: number | null
  quality: string
  path: string
  /** 手改过的 path 在改集号 / 类型时不被推测覆盖 */
  pathDirty: boolean
}

function makeSingleLine(seed?: Partial<SingleLine>): SingleLine {
  return {
    id: newClientId(),
    source_id: seed?.source_id ?? null,
    quality: seed?.quality ?? '',
    path: seed?.path ?? '',
    pathDirty: seed?.pathDirty ?? false,
  }
}

function previousForLine(
  prevMap: Map<string, PreviousChannelPath>,
  line: Pick<SingleLine, 'source_id' | 'quality'>,
): PreviousChannelPath | undefined {
  if (line.source_id == null) return undefined
  return prevMap.get(channelGuessKey(line.source_id, line.quality || null))
}

function applyGuessedPath(
  line: SingleLine,
  prevMap: Map<string, PreviousChannelPath>,
  nextEp: number,
  mode: 'clean' | 'all',
): SingleLine {
  if (mode === 'clean' && line.pathDirty) return line
  const guess = guessLinePath(previousForLine(prevMap, line), nextEp)
  if (!guess || line.path === guess) return line
  return { ...line, path: guess, pathDirty: false }
}

function newClientId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `c_${Math.random().toString(36).slice(2)}`
}

/** 按 kind + 集号生成默认标题；用户改过的不会被覆盖（由调用方追踪 dirty） */
function autoEpisodeTitle(n: number, kind: string): string {
  switch (kind) {
    case 'main':
      return `第 ${n} 集`
    case 'sp':
      return n > 1 ? `特别篇 ${n}` : '特别篇'
    case 'op':
      return n > 1 ? `OP ${n}` : 'OP'
    case 'ed':
      return n > 1 ? `ED ${n}` : 'ED'
    default:
      return ''
  }
}

function SourceBaseUrlHint({
  baseUrl,
  sourceCode,
}: {
  baseUrl: string | null | undefined
  sourceCode?: string | null
}) {
  const aliases = pastePrefixAliases(sourceCode)
  if (!baseUrl && aliases.length === 0) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        该源无 base_url，path 将原样入库
      </Typography.Text>
    )
  }
  return (
    <>
      {baseUrl ? (
        <Typography.Text
          className="admin-source-base-url"
          copyable={{ text: baseUrl, tooltips: ['复制 base_url', '已复制'] }}
          title={baseUrl}
        >
          {baseUrl}
        </Typography.Text>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          该源无 base_url，path 将原样入库
        </Typography.Text>
      )}
      {aliases.length > 0 ? (
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, display: 'block' }}
        >
          也接受前缀：{aliases.join('、')}
        </Typography.Text>
      ) : null}
    </>
  )
}

function AddChannelPopover({
  sources,
  existing,
  disabled,
  compact,
  onAdd,
}: {
  sources: Array<PlaySource>
  existing: Array<ColKey>
  disabled?: boolean
  /** 手机工具栏：只显示图标 */
  compact?: boolean
  onAdd: (k: ColKey) => boolean
}) {
  const { message } = AntdApp.useApp()
  const [open, setOpen] = useState(false)
  const [sourceId, setSourceId] = useState<number | null>(null)

  function reset() {
    setSourceId(null)
  }

  function handleAdd() {
    if (sourceId == null) {
      message.error('先选播放源')
      return
    }
    const ok = onAdd({
      source_id: sourceId,
      quality: null,
    })
    if (!ok) return
    message.success('已加列，填写路径后点保存')
    reset()
    setOpen(false)
  }

  const existingIds = new Set(existing.map(colKeyId))
  const selectedKey =
    sourceId == null ? null : colKeyId({ source_id: sourceId, quality: null })
  const alreadyThere = selectedKey != null && existingIds.has(selectedKey)

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
      title="加线路列"
      content={
        <Space
          direction="vertical"
          size={8}
          style={{ width: 'min(280px, calc(100vw - 56px))' }}
        >
          <Select
            placeholder="选播放源"
            value={sourceId ?? undefined}
            onChange={(v) => setSourceId(v)}
            options={sources.map((s) => ({
              value: s.id,
              label: `${s.name} (${s.code})`,
            }))}
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
          />
          {alreadyThere ? (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              这一列已经在表里
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              加列后不会立刻写库；往单元格填 path 再保存才新建。
            </Typography.Text>
          )}
          <Button
            type="primary"
            block
            disabled={sourceId == null || alreadyThere}
            onClick={handleAdd}
          >
            加到表格
          </Button>
        </Space>
      }
    >
      <Tooltip title={compact ? '加线路' : undefined}>
        <Button
          icon={<InsertRowRightOutlined />}
          disabled={disabled}
          aria-label="加线路"
        >
          {compact ? null : '加线路'}
        </Button>
      </Tooltip>
    </Popover>
  )
}

/* ── 删线路：把本番剧里某条线路（该源的全部画质）整列摘掉 ──────
   走 admin.delete_anime_play_source：只删本番 episode_play_urls，
   不动全局 play_sources 记录，也不删剧集。两步：选源 → 看影响面 → 确认。 */
function RemoveSourcePopover({
  options,
  disabled,
  compact,
  submitting,
  onRemove,
}: {
  options: Array<RemovableSource>
  disabled?: boolean
  /** 手机工具栏：只显示图标 */
  compact?: boolean
  submitting?: boolean
  onRemove: (s: RemovableSource) => void
}) {
  const [open, setOpen] = useState(false)
  const [pickedId, setPickedId] = useState<number | null>(null)

  const picked = options.find((o) => o.source_id === pickedId) ?? null

  function reset() {
    setPickedId(null)
  }

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
      title="摘除线路"
      content={
        <Space
          direction="vertical"
          size={8}
          style={{ width: 'min(300px, calc(100vw - 56px))' }}
        >
          <Select
            placeholder="选要摘除的线路"
            value={pickedId ?? undefined}
            onChange={(v) => setPickedId(v)}
            options={options.map((o) => ({
              value: o.source_id,
              label: `${o.name} (${o.code})`,
            }))}
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
          />
          {picked ? (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                将从本番剧删除该线路的 {picked.urlCount} 条地址（
                {picked.episodeCount} 集
                {picked.qualities.length > 0
                  ? `，画质 ${picked.qualities.join(' / ')}`
                  : ''}
                ）。全局线路与剧集保留；已转码的 CDN 文件不自动清。
              </Typography.Text>
              <Button
                danger
                type="primary"
                block
                loading={submitting}
                onClick={() => {
                  onRemove(picked)
                  setOpen(false)
                  reset()
                }}
              >
                确认摘除
              </Button>
            </>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              只对本番剧生效：删除该源在所有集上的地址行，
              线路本身保留可给其它番剧继续用。
            </Typography.Text>
          )}
        </Space>
      }
    >
      <Tooltip title={compact ? '删线路（整列摘除）' : undefined}>
        <Button
          icon={<DeleteOutlined />}
          disabled={disabled}
          aria-label="删线路"
        >
          {compact ? null : '删线路'}
        </Button>
      </Tooltip>
    </Popover>
  )
}

function AddEpisodesDrawer({
  open,
  onClose,
  defaultStart,
  sources,
  existingChannels,
  episodes,
  allowEmptyChannels = false,
  submitting,
  onSubmit,
}: {
  open: boolean
  onClose: () => void
  defaultStart: number
  sources: Array<PlaySource>
  existingChannels: Array<ColKey>
  episodes: Array<EpisodeRow>
  allowEmptyChannels?: boolean
  submitting: boolean
  onSubmit: (payload: Array<EpisodePayload>) => void
}) {
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()

  /* ── 模式切换：默认单集追更（更贴合"加下一集"语义），批量保留为备用 ── */
  const [mode, setMode] = useState<'single' | 'batch'>('single')

  /* ── 公共：集号 / 类型（两模式都用） ────────────────────────── */
  const [startEp, setStartEp] = useState<number>(defaultStart)
  const [kind, setKind] = useState<string>('main')

  /* ── 单集模式：title 自动生成 + 一行一线路 ──────────────────── */
  const [singleTitle, setSingleTitle] = useState<string>('')
  // 用户手动改过 title 后，集号/类型变化不再覆盖
  const [titleDirty, setTitleDirty] = useState<boolean>(false)
  const [singleLines, setSingleLines] = useState<Array<SingleLine>>([])

  /* ── 批量模式：原有按列粘贴 ───────────────────────────────── */
  const [cols, setCols] = useState<Array<PasteColumn>>([])

  const previousByChannel = useMemo(
    () => indexPreviousChannelPaths(episodes, kind, startEp),
    [episodes, kind, startEp],
  )

  // 打开时重置：默认预填本番剧既有的所有 (source, quality) 组合，并按上一集 path 推测
  // dep 只包 open —— 一旦打开后，后台 query 刷新带来的 defaultStart/existingChannels
  // 变化不应覆盖用户已经输入的内容
  useEffect(() => {
    if (!open) return
    setStartEp(defaultStart)
    setKind('main')
    setMode('single')
    setTitleDirty(false)
    setSingleTitle(autoEpisodeTitle(defaultStart, 'main'))
    const prevMap = indexPreviousChannelPaths(episodes, 'main', defaultStart)
    if (existingChannels.length > 0) {
      setSingleLines(
        existingChannels.map((c) =>
          applyGuessedPath(
            makeSingleLine({
              source_id: c.source_id,
              quality: c.quality ?? '',
            }),
            prevMap,
            defaultStart,
            'all',
          ),
        ),
      )
      setCols(
        existingChannels.map((c) =>
          makeColumn({ source_id: c.source_id, quality: c.quality ?? '' }),
        ),
      )
    } else {
      setSingleLines([makeSingleLine()])
      setCols([makeColumn()])
    }
  }, [open])

  // 抽屉打开时 episodes 可能还没回来；历史到了只补空的、未手改的行
  useEffect(() => {
    if (!open || previousByChannel.size === 0) return
    setSingleLines((prev) => {
      const next = prev.map((l) => {
        if (l.pathDirty || l.path.trim()) return l
        return applyGuessedPath(l, previousByChannel, startEp, 'clean')
      })
      return next.every((line, i) => line === prev[i]) ? prev : next
    })
  }, [open, previousByChannel, startEp])

  // 集号/类型变化时自动重算 title（用户手改过的不动）
  useEffect(() => {
    if (titleDirty) return
    setSingleTitle(autoEpisodeTitle(startEp, kind))
  }, [startEp, kind, titleDirty])

  const sourceById = useMemo(() => {
    const m = new Map<number, PlaySource>()
    for (const s of sources) m.set(s.id, s)
    return m
  }, [sources])

  // RPC 后到的 base_url 补上后，把已粘贴的完整 URL 再裁一次
  useEffect(() => {
    setSingleLines((prev) => {
      let changed = false
      const next = prev.map((l) => {
        if (l.source_id == null || !l.path) return l
        const trimmed = cleanPath(
          l.path,
          sourceById.get(l.source_id)?.base_url ?? '',
          sourceById.get(l.source_id)?.code,
        )
        if (trimmed === l.path) return l
        changed = true
        return { ...l, path: trimmed }
      })
      return changed ? next : prev
    })
    setCols((prev) => {
      let changed = false
      const next = prev.map((c) => {
        if (c.source_id == null || !c.text) return c
        const trimmed = cleanPastedLines(
          c.text,
          sourceById.get(c.source_id)?.base_url ?? '',
          sourceById.get(c.source_id)?.code,
        )
        if (trimmed === c.text) return c
        changed = true
        return { ...c, text: trimmed }
      })
      return changed ? next : prev
    })
  }, [sourceById])

  // 解析：每列 trim 出非空行
  const parsed = useMemo(() => {
    return cols.map((c) => ({
      col: c,
      lines: c.text
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0),
    }))
  }, [cols])

  const maxRows = parsed.reduce((m, p) => Math.max(m, p.lines.length), 0)
  const totalChannels = parsed.reduce((n, p) => n + p.lines.length, 0)
  const misalignedCols = parsed.filter(
    (p) => p.lines.length > 0 && p.lines.length !== maxRows,
  )

  function setCol(id: string, patch: Partial<PasteColumn>) {
    setCols((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }
  function removeCol(id: string) {
    setCols((prev) => prev.filter((c) => c.id !== id))
  }
  function addCol() {
    setCols((prev) => [...prev, makeColumn()])
  }

  /* ── 单集模式：线路操作 + 提交 ────────────────────────────── */
  function setSingleLine(id: string, patch: Partial<SingleLine>) {
    setSingleLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    )
  }
  function removeSingleLine(id: string) {
    setSingleLines((prev) => prev.filter((l) => l.id !== id))
  }
  function addSingleLine() {
    setSingleLines((prev) => [...prev, makeSingleLine()])
  }
  function syncGuesses(nextStart: number, nextKind: string) {
    const prevMap = indexPreviousChannelPaths(episodes, nextKind, nextStart)
    setSingleLines((prev) =>
      prev.map((l) => applyGuessedPath(l, prevMap, nextStart, 'clean')),
    )
  }
  function fillLineGuess(id: string) {
    setSingleLines((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l
        return applyGuessedPath(l, previousByChannel, startEp, 'all')
      }),
    )
  }
  function fillAllGuesses() {
    const next = singleLines.map((l) =>
      applyGuessedPath(l, previousByChannel, startEp, 'all'),
    )
    const n = next.filter((l, i) => l.path !== singleLines[i].path).length
    setSingleLines(next)
    if (n === 0) {
      const already = singleLines.filter((l) => {
        const guess = guessLinePath(
          previousForLine(previousByChannel, l),
          startEp,
        )
        return guess != null && guess === l.path
      }).length
      if (already > 0) {
        message.info(`已经是推测路径（${already} 条）`)
        return
      }
      message.info('没有可推测的路径')
      return
    }
    message.success(`已填入 ${n} 条推测路径`)
  }

  const guessableCount = singleLines.filter(
    (l) =>
      guessLinePath(previousForLine(previousByChannel, l), startEp) != null,
  ).length

  const filledSingleLines = singleLines.filter(
    (l) => l.source_id != null && l.path.trim().length > 0,
  )

  function handleSubmitSingle() {
    const channels: Array<ChannelPayload> = []
    for (const l of filledSingleLines) {
      const src = sourceById.get(l.source_id!)
      if (!src) continue
      // onChange 已清过一遍，这里兜底（防 source 换了导致 prefix 不再匹配）
      const cleaned = cleanPath(l.path.trim(), src.base_url ?? '', src.code)
      channels.push({
        source_code: src.code,
        path: cleaned,
        quality: l.quality.trim() || null,
      })
    }
    if (channels.length === 0 && !allowEmptyChannels) {
      message.error('至少填一条线路（source + path）')
      return
    }
    if (channels.length === 0 && !singleTitle.trim()) {
      message.error('至少填标题，或填一条线路')
      return
    }
    const payload: EpisodePayload = {
      episode_number: startEp,
      kind,
      title: singleTitle.trim() || null,
    }
    if (channels.length > 0) payload.channels = channels
    onSubmit([payload])
  }

  function handleSubmit() {
    if (maxRows === 0) {
      message.error('至少粘一行链接')
      return
    }
    if (cols.some((c) => c.source_id == null && c.text.trim() !== '')) {
      message.error('每条有内容的线路都要选 source')
      return
    }

    // 行 i → 集号 = startEp + i；按列填 channels
    const payload: Array<EpisodePayload> = []
    for (let i = 0; i < maxRows; i++) {
      const channels: Array<ChannelPayload> = []
      for (const p of parsed) {
        const url = p.lines[i]
        if (!url) continue
        if (p.col.source_id == null) continue
        const src = sourceById.get(p.col.source_id)
        if (!src) continue
        // 提交时清洗：粘贴的完整 URL 自动去掉 source 前缀，避免拼成 prefix+完整URL
        channels.push({
          source_code: src.code,
          path: cleanPath(url, src.base_url ?? '', src.code),
          quality: p.col.quality.trim() || null,
        })
      }
      if (channels.length === 0) continue
      payload.push({
        episode_number: startEp + i,
        kind,
        channels,
      })
    }
    if (payload.length === 0) {
      message.error('没有有效数据')
      return
    }
    onSubmit(payload)
  }

  return (
    <Drawer
      className="admin-followup-drawer"
      open={open}
      onClose={onClose}
      title="新增剧集"
      width={isMobile ? '100%' : mode === 'single' ? 800 : '80vw'}
      destroyOnClose
      styles={{ footer: { padding: '14px 24px' } }}
      footer={
        mode === 'single' ? (
          <Space
            className="episodes-drawer-footer"
            style={{ width: '100%', justifyContent: 'space-between' }}
          >
            <Typography.Text type="secondary">
              {filledSingleLines.length > 0
                ? `将提交 1 集 · ${filledSingleLines.length} 条线路`
                : allowEmptyChannels
                  ? '可以只建一集、路径稍后在表格里填'
                  : '尚未填线路'}
            </Typography.Text>
            <Space>
              <Button onClick={onClose}>取消</Button>
              <Button
                type="primary"
                loading={submitting}
                onClick={handleSubmitSingle}
                disabled={
                  filledSingleLines.length === 0 &&
                  !(allowEmptyChannels && singleTitle.trim())
                }
              >
                提交本集 (merge)
              </Button>
            </Space>
          </Space>
        ) : (
          <Space
            className="episodes-drawer-footer"
            style={{ width: '100%', justifyContent: 'space-between' }}
          >
            <Typography.Text type="secondary">
              {maxRows > 0
                ? `将提交 ${maxRows} 集 × ${cols.filter((c) => c.text.trim()).length} 条线路 = ${totalChannels} 个 channel`
                : '尚未粘贴任何链接'}
            </Typography.Text>
            <Space>
              <Button onClick={onClose}>取消</Button>
              <Button
                type="primary"
                loading={submitting}
                onClick={handleSubmit}
                disabled={maxRows === 0}
              >
                提交导入 (merge)
              </Button>
            </Space>
          </Space>
        )
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Segmented<'single' | 'batch'>
          block
          value={mode}
          onChange={setMode}
          options={[
            { value: 'single', label: '剧集追更' },
            { value: 'batch', label: '批量导入' },
          ]}
        />

        {/* 公共参数：集号 / 类型；单集模式再叠 title */}
        <Space size="middle" wrap>
          <Space size={4}>
            <Typography.Text>
              {mode === 'single' ? '集号' : '起始集号'}
            </Typography.Text>
            <InputNumber
              value={startEp}
              onChange={(v) => {
                const n = Number(v) || 1
                setStartEp(n)
                syncGuesses(n, kind)
              }}
              min={0}
              step={1}
              style={{ width: 100 }}
            />
          </Space>
          <Space size={4}>
            <Typography.Text>类型</Typography.Text>
            <Select
              value={kind}
              onChange={(nextKind) => {
                setKind(nextKind)
                syncGuesses(startEp, nextKind)
              }}
              options={KIND_OPTIONS}
              style={{ width: 160 }}
            />
          </Space>
        </Space>

        {mode === 'single' ? (
          <>
            <Space size={4} style={{ width: '100%' }}>
              <Typography.Text style={{ flexShrink: 0 }}>标题</Typography.Text>
              <Input
                value={singleTitle}
                onChange={(e) => {
                  setSingleTitle(e.target.value)
                  setTitleDirty(true)
                }}
                placeholder={autoEpisodeTitle(startEp, kind) || '可空'}
                maxLength={200}
                suffix={
                  titleDirty ? (
                    <Typography.Link
                      style={{ fontSize: 12 }}
                      onClick={() => {
                        setTitleDirty(false)
                        setSingleTitle(autoEpisodeTitle(startEp, kind))
                      }}
                    >
                      重置自动
                    </Typography.Link>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      自动
                    </Typography.Text>
                  )
                }
              />
            </Space>

            <div className="admin-drawer-lines">
              <div className="admin-drawer-lines__head">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  线路按上一集 path 用 / 分段推测，剧集序号 +1。可改。
                </Typography.Text>
                <Button
                  size="small"
                  disabled={guessableCount === 0}
                  onClick={fillAllGuesses}
                >
                  一键填入推测
                  {guessableCount > 0 ? ` (${guessableCount})` : ''}
                </Button>
              </div>
              {singleLines.map((l) => {
                const src =
                  l.source_id != null ? sourceById.get(l.source_id) : undefined
                const prefix = src?.base_url ?? ''
                const previous = previousForLine(previousByChannel, l)
                const guess = guessLinePath(previous, startEp)
                const guessed = guess != null && guess === l.path
                return (
                  <div key={l.id} className="admin-drawer-line">
                    {/* 手机：源 + 删除一行，path 独占下一行（Col order 重排，不复制 JSX） */}
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex={isMobile ? '1 1 0' : '220px'} order={1}>
                        <Select
                          placeholder="选 source"
                          value={l.source_id ?? undefined}
                          onChange={(v) => {
                            const nextSrc = sourceById.get(v)
                            const nextLine = {
                              ...l,
                              source_id: v,
                              path: cleanPath(
                                l.path,
                                nextSrc?.base_url ?? '',
                                nextSrc?.code,
                              ),
                            }
                            const nextGuess = guessLinePath(
                              previousForLine(previousByChannel, nextLine),
                              startEp,
                            )
                            const shouldGuess = !l.pathDirty || !l.path.trim()
                            setSingleLine(l.id, {
                              source_id: v,
                              path:
                                shouldGuess && nextGuess
                                  ? nextGuess
                                  : nextLine.path,
                              pathDirty: shouldGuess ? false : l.pathDirty,
                            })
                          }}
                          options={sources.map((s) => ({
                            value: s.id,
                            label: `${s.name} (${s.code})`,
                          }))}
                          showSearch
                          optionFilterProp="label"
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col
                        flex={isMobile ? '0 0 100%' : 'auto'}
                        order={isMobile ? 3 : 2}
                        style={{ minWidth: 0 }}
                      >
                        <Input
                          className={
                            !l.pathDirty && guessed
                              ? 'admin-drawer-path--guess'
                              : undefined
                          }
                          value={l.path}
                          onChange={(e) =>
                            setSingleLine(l.id, {
                              path: cleanPath(e.target.value, prefix),
                              pathDirty: true,
                            })
                          }
                          placeholder={
                            guess ??
                            (prefix
                              ? '相对路径，如 12.m3u8（粘贴完整 URL 会自动剪掉前缀）'
                              : 'path 或完整 URL（source 无 base_url）')
                          }
                          style={{ fontFamily: 'monospace' }}
                          suffix={
                            guess ? (
                              <Tooltip
                                title={
                                  previous
                                    ? `根据第 ${previous.episodeNumber} 集：${previous.path}`
                                    : undefined
                                }
                              >
                                <Typography.Link
                                  style={{ fontSize: 12 }}
                                  disabled={guessed}
                                  onClick={() => fillLineGuess(l.id)}
                                >
                                  {guessed ? '已推测' : '填入'}
                                </Typography.Link>
                              </Tooltip>
                            ) : null
                          }
                        />
                      </Col>
                      <Col order={isMobile ? 2 : 3}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => removeSingleLine(l.id)}
                          aria-label="删除该线路"
                        />
                      </Col>
                    </Row>
                    <SourceBaseUrlHint
                      baseUrl={src?.base_url}
                      sourceCode={src?.code}
                    />
                  </div>
                )
              })}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addSingleLine}
                block
              >
                加线路
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* 对齐提示（批量） */}
            {misalignedCols.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message="线路行数不一致"
                description={`最长 ${maxRows} 行，其他列短缺的集会自动跳过（merge 模式不影响该线路既有数据）`}
              />
            ) : null}

            {/* 列容器（批量） */}
            <div className="admin-drawer-columns">
              {cols.map((c) => {
                const lineCount =
                  parsed.find((p) => p.col.id === c.id)?.lines.length ?? 0
                return (
                  <div key={c.id} className="admin-drawer-column">
                    <Space size={4} style={{ justifyContent: 'space-between' }}>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        线路
                      </Typography.Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeCol(c.id)}
                        aria-label="删除该线路"
                      />
                    </Space>
                    <Select
                      placeholder="选 source"
                      value={c.source_id ?? undefined}
                      onChange={(v) => {
                        const nextSrc = sourceById.get(v)
                        setCol(c.id, {
                          source_id: v,
                          text: cleanPastedLines(
                            c.text,
                            nextSrc?.base_url ?? '',
                            nextSrc?.code,
                          ),
                        })
                      }}
                      options={sources.map((s) => ({
                        value: s.id,
                        label: `${s.name} (${s.code})`,
                      }))}
                      showSearch
                      optionFilterProp="label"
                    />
                    <SourceBaseUrlHint
                      baseUrl={
                        c.source_id != null
                          ? sourceById.get(c.source_id)?.base_url
                          : undefined
                      }
                      sourceCode={
                        c.source_id != null
                          ? sourceById.get(c.source_id)?.code
                          : undefined
                      }
                    />
                    <Input.TextArea
                      value={c.text}
                      onChange={(e) => {
                        const src =
                          c.source_id != null
                            ? sourceById.get(c.source_id)
                            : undefined
                        setCol(c.id, {
                          text: cleanPastedLines(
                            e.target.value,
                            src?.base_url ?? '',
                            src?.code,
                          ),
                        })
                      }}
                      placeholder={
                        c.source_id != null &&
                        sourceById.get(c.source_id)?.base_url
                          ? '每行一条 URL\n粘贴完整链接会自动裁掉该源 base_url'
                          : '每行一条 URL\n空行会被跳过'
                      }
                      autoSize={{ minRows: 8, maxRows: 20 }}
                      style={{ fontFamily: 'monospace', fontSize: 13 }}
                    />
                    <Typography.Text
                      type={
                        lineCount === maxRows || lineCount === 0
                          ? 'secondary'
                          : 'warning'
                      }
                      style={{ fontSize: 12 }}
                    >
                      {lineCount} 行
                      {maxRows > 0 && lineCount > 0 && lineCount !== maxRows
                        ? `（与最长列差 ${maxRows - lineCount}）`
                        : ''}
                    </Typography.Text>
                  </div>
                )
              })}
              <div
                style={
                  isMobile
                    ? undefined
                    : { flex: '0 0 120px', alignSelf: 'flex-start' }
                }
              >
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addCol}
                  block={isMobile}
                  style={
                    isMobile
                      ? undefined
                      : { width: '100%', height: '100%', minHeight: 120 }
                  }
                >
                  加线路
                </Button>
              </div>
            </div>
          </>
        )}
      </Space>
    </Drawer>
  )
}
